/* eslint-disable i18next/no-literal-string */
/* eslint-disable prettier/prettier */
import React, { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { AxiosResponse } from "axios";
import errorCaution from "../../../public/images/svg/errorCaution.svg";
import { AppSettingsService } from "@/service/AppSettingsService";
import api from "@/api/api";
import Logger from "@/utils/Logger";

// Constants
const CONNECTION_CHECK_INTERVAL = 5000;
const MAX_RETRY_ATTEMPTS = 3;

// Component
const ConnectionErrorPage: React.FC = () => {
    const navigate = useNavigate();
    const intervalRef = useRef<NodeJS.Timeout | null>(null);
    const retryCountRef = useRef<number>(0);

    const useInternalApi = AppSettingsService.shouldUseInternalApi();
    const internalApiUrl = AppSettingsService.getInternalApiUrl();

    useEffect(() => {
        // Early return and redirect if not using internal API
        if (!useInternalApi) {
            Logger.info("Connection error page accessed without internal API - redirecting to home", ["ConnectionErrorPage"]);
            navigate("/", { replace: true });
            return;
        }

        const checkConnection = (): void => {
            api
                .get("/healthcheck", { baseURL: internalApiUrl })
                .then((response: AxiosResponse) => {
                    if (response.status === 200) {
                        Logger.info("Connection restored, redirecting to home page", ["ConnectionErrorPage"]);

                        if (intervalRef.current) {
                            clearInterval(intervalRef.current);
                        }

                        navigate("/", { replace: true });
                    }
                })
                .catch((error) => {
                    retryCountRef.current += 1;
                    Logger.debug(`Connection check failed (attempt ${retryCountRef.current})`, ["ConnectionErrorPage"]);

                    // Stop checking after max retries to prevent infinite loops
                    if (retryCountRef.current >= MAX_RETRY_ATTEMPTS) {
                        Logger.warn("Max retry attempts reached, will continue checking at interval", ["ConnectionErrorPage"]);
                        retryCountRef.current = 0; // Reset for next cycle
                    }
                });
        };

        // Initial check immediately
        checkConnection();

        // set interval after initial check
        intervalRef.current = setInterval(checkConnection, CONNECTION_CHECK_INTERVAL);

        return () => {
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
                intervalRef.current = null;
            }
        };
    }, [useInternalApi, internalApiUrl, navigate]);

    // Don't render anything while redirecting
    if (!useInternalApi) {
        return null;
    }

    const renderErrorIcon = () => (
        <div className="flex justify-center items-center pt-20 pb-5">
            <img
                className="w-[100px]"
                src={errorCaution}
                alt="Connection error warning"
            />
        </div>
    );

    const hrefUrl = `${internalApiUrl}healthcheck`;

    const renderErrorMessage = () => (
        <div className="space-y-8 mt-6">
            <div className="space-y-4">
                <h1 className="text-[#005DAB] text-5xl font-light tracking-wide">
                    Connection Issue
                </h1>
                <p className="text-xl text-gray-600 font-light">
                    Unable to establish connection to the backend service
                </p>
            </div>

            <div className="max-w-2xl mx-auto space-y-6 px-6">
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 space-y-6">
                    <div className="space-y-4 text-center">
                        <h2 className="text-xl font-medium text-gray-800 mb-4">
                            Please verify the following:
                        </h2>
                        <ul className="space-y-3 text-gray-700 inline-block text-left">
                            <li className="flex items-start space-x-3">
                                <span className="flex-shrink-0 w-2 h-2 bg-blue-500 rounded-full mt-2"></span>
                                <span className="text-xl">Your internet connection is stable and active</span>
                            </li>
                            <li className="flex items-start space-x-3">
                                <span className="flex-shrink-0 w-2 h-2 bg-blue-500 rounded-full mt-2"></span>
                                <span className="text-xl">You are connected to the Costco VPN network</span>
                            </li>
                        </ul>
                    </div>
                </div>

                <div className="bg-blue-50 bg-white rounded-xl border border-blue-200 p-6 text-center">
                    <div className="flex flex-col items-center space-y-3">
                        <div className="flex-shrink-0">
                            <svg className="w-5 h-5 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                            </svg>
                        </div>
                        <div className="space-y-2">
                            <p className="text-xl font-medium text-blue-800">
                                First time accessing SCRM Portal?
                            </p>
                            <p className="text-xl text-blue-700">
                                You may need to establish trust with our server certificate.
                            </p>
                            <p className="text-xl text-blue-700">
                                <a
                                    className="font-medium text-blue-600 hover:text-blue-500 underline transition-colors duration-200"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    href={hrefUrl}
                                >
                                    Click here to trust the server
                                </a>
                                , then refresh this page.
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );

    return (
        <div className="pt-12 bg-gray-100 h-screen justify-center text-center text-CostcoAction font-Helvetica">
            <div className="mx-auto max-w-4xl">
                <div className="pb-5">
                    {renderErrorIcon()}
                    {renderErrorMessage()}
                </div>
            </div>
        </div>
    );
};

export default ConnectionErrorPage;
